import { accountEdit, accountRisksSettingsEdit, accountUpdate } from "@/redux/account.actions"
/**
 * ESTABLISHMENT SYNC MODAL
 * Display accountRisksSettings details before synchronization.
 */
import Button from "@/components/button"
import Checkbox from "@/components/checkbox"
import Modal from "@/components/modal"
import Space from "@/components/space"
import TextInput from "@/components/text-input"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import {
  createEstablishmentFromSirene,
  fetchActiveEstablishmentsBySiren,
  SireneEstablishment
} from "@/entities/establishment/model/establishment.sirene"
import { getLegalIdentifier } from "@/redux/account.types"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { faArrowRight, faSyncAlt, faTimes } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { useState } from "react"
import { toast } from "react-toastify"
import ListItem from "@/components/list-item"

interface Props {
  onClose: () => void
}

export default function EstablishmentSyncModal({ onClose }: Props) {
  const dispatch = useAppDispatch()
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const account = useAppSelector((state) => state.account.active)
  const establishments = useAppSelector(establishmentStore.selectors.list)
  const status = useAppSelector(establishmentStore.selectors.status)
  const [accountName, setAccountName] = useState(account.name)
  const [legalIdentifier, setLegalIdentifier] = useState(getLegalIdentifier(accountRisksSettings))
  const [items, setItems] = useState<SireneEstablishment[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const cleanSiren = legalIdentifier.replace(/\D/g, "")
  const canSync = cleanSiren.length === 9 && !isSearching
  const selectedItems = items.filter((item) => selectedIds.includes(item.id))

  const updateLegalIdentifier = ({ value }: { value: string }) => {
    setLegalIdentifier(value)
    setHasSearched(false)
    setItems([])
    setSelectedIds([])
  }

  const updateAccountName = ({ value }: { value: string }) => {
    setAccountName(value)
    setHasSearched(false)
    setItems([])
    setSelectedIds([])
  }

  const toggleItem = (id: string, value: boolean) => {
    setSelectedIds((current) => (value ? [...current, id] : current.filter((itemId) => itemId !== id)))
  }

  async function saveAccountInfosIfNeeded() {
    const hasCodeChanged = cleanSiren !== getLegalIdentifier(accountRisksSettings).replace(/\D/g, "")
    const hasNameChanged = accountName !== account.name

    if (!hasCodeChanged && !hasNameChanged) return

    dispatch(accountEdit("name", accountName))
    dispatch(accountRisksSettingsEdit({ key: "legalIdentifier", value: cleanSiren }))
    await dispatch(accountUpdate(store.getState().account.active))
  }

  async function syncEstablishments() {
    if (!canSync) return

    setIsSearching(true)
    try {
      await saveAccountInfosIfNeeded()
      const fetchedItems = await fetchActiveEstablishmentsBySiren(cleanSiren, accountName)
      const importedSirets = new Set(establishments.map((establishment) => establishment.codeEstablishment))
      const importableItems = fetchedItems.filter((item) => !importedSirets.has(item.siret))

      setItems(importableItems)
      setSelectedIds(importableItems.map((item) => item.id))
      setHasSearched(true)

      if (!importableItems.length) {
        toast("Aucun établissement actif à importer pour ce SIREN.", { type: "info" })
      }
    } catch (error) {
      console.log(error)
      toast("Impossible de synchroniser les établissements.", { type: "error" })
    } finally {
      setIsSearching(false)
    }
  }

  async function importSelectedEstablishments() {
    if (!selectedItems.length) {
      toast("Sélectionnez au moins un établissement à importer.", { type: "warning" })
      return
    }

    try {
      for (const item of selectedItems) {
        await dispatch(establishmentStore.thunks.createEntity(createEstablishmentFromSirene(item))).unwrap()
      }
      toast(`${selectedItems.length} établissement(s) importé(s).`, { type: "success" })
      onClose()
    } catch (error) {
      console.log(error)
      toast("Impossible d'importer les établissements sélectionnés.", { type: "error" })
    }
  }

  return (
    <Modal
      isLarge
      title="Synchroniser les établissements"
      footer={
        <div className="flex w-full m-[-5px] mt-20">
          <Space />
          <Button onClick={onClose} className="med" icon={faTimes} isWithBorder>
            {t("utils_close")}
          </Button>
          {hasSearched ? (
            <Button
              onClick={importSelectedEstablishments}
              className={selectedItems.length ? "primary" : "light"}
              icon={faArrowRight}
              isLoading={status === "STATUS_SAVING"}
              disabled={!selectedItems.length}>
              Importer
            </Button>
          ) : (
            <Button
              onClick={syncEstablishments}
              className={canSync ? "primary" : "light"}
              icon={faSyncAlt}
              isLoading={isSearching}
              disabled={!canSync}>
              Sync
            </Button>
          )}
        </div>
      }>
      <div className="flex flex-col gap-4">
        <TextInput title="Nom de l'organisation" value={accountName} onChange={updateAccountName} />
        <TextInput title="Code SIREN" value={legalIdentifier} onChange={updateLegalIdentifier} />

        {hasSearched && (
          <div className="flex flex-col gap-2">
            <div className="text-input-title flex grey-t">Établissements actifs trouvés</div>

            {items.length === 0 ? (
              <div className="grey-t font-bold">Aucun nouvel établissement actif trouvé.</div>
            ) : (
              items.map((item) => (
                <ListItem key={item.id}>
                  <Checkbox
                    active={selectedIds.includes(item.id)}
                    onClick={(event) => toggleItem(item.id, event.value)}
                  />
                  <div className="flex flex-col ml-[32px]">
                    <div className="font-bold">{item.name}</div>
                    <div className="grey-t">{item.address || "Adresse non renseignée"}</div>
                    <div className="grey-t">
                      SIRET {item.siret} {item.industryCode ? `- NAF ${item.industryCode}` : ""}
                    </div>
                  </div>
                  <Space />
                </ListItem>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
